/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * A pick's sheet (2026-10-17): what the action row's Measurements and Owners open below `md`,
 * instead of expanding inline — on a phone an inline lab panel per pick made the guide some
 * 34,000px tall, and the owner notes printed twice — and at every width from the "Our picks"
 * grid card, which has no room to expand. One sheet per pick, the
 * community's `BottomSheet` (modal, scroll-locked, swipe-down to close): the pick's name and
 * "vs your …", then three segments, the desktop panel's (2026-10-19) — "This pick" (its chips
 * and lab panel), "Owners · n", and "Compare · n of 3" (of 2 beside a declared pair), the pinned
 * comparison itself (`LabCompareBody`, the panel's body; the sheet no longer hands off to the
 * drawer) — and in the foot the same pin the row has, with the compare tile on its right, on
 * every segment. No segment is disabled: an
 * empty one is muted, says why in its `title`, and opens on its empty state.
 *
 * Layout (`BottomSheet contentScroll`): the head and the segments stay put, the body scrolls
 * (`data-testid="pick-sheet-body"`), the foot stays in view below it. The Compare chart's frame
 * follows the sheet's content width (viewport, capped like the CSS, less the padding), read on
 * resize — never the breakpoint, never observed (Directive 427141fd).
 *
 * Client-only by construction: the sheet mounts on a tap, so nothing here is in the
 * edge-cached HTML (#51). The owners segment prints the notes a second time on the page (the
 * roster row's copy is in the HTML, hidden on a phone) — each note is still served once.
 */
import { useEffect, useState } from 'react'
import clsx from 'clsx'
import styles from './PickSheet.module.css'
import PickLabPanel from './PickLabPanel'
import PickOwners from './PickOwners'
import { CompareTile } from './PickFocusBody'
import LabCompareBody from '../LabCompareBody'
import PinToCompare from '../PinToCompare'
import BottomSheet from 'components/community/BottomSheet'
import type { LabChartPaths } from 'lib/dealhunt/audioChart'
import type { OwnerTrack } from 'lib/owner-notes/analytics'
import { GUIDE_COPY } from 'lib/owner-notes/band-copy'
import type { CardOwnersView } from 'lib/owner-notes/card-owners'
import type { DeclaredProduct } from 'lib/owner-notes/declared'
import { chartFrameFor, useLabCompare, type PanelSegment } from 'lib/owner-notes/lab-compare'
import type { DSGuidePickRef } from 'types.sg.ds'

// The host's width on the first render (client-only mounts), so the chart frame does not
// start compact and jump once an effect has read it (2026-10-19).
const initialViewportW = (): number => (typeof window === 'undefined' ? 0 : window.innerWidth)

/** The sheet's segments are the desktop panel's: this pick, its owners, the pinned comparison. */
export type PickSheetSegment = PanelSegment

/** The sheet's horizontal padding (module `.sheet`: 16 + 16), taken off its width for the frame. */
const SHEET_PAD_X = 32
/** The sheet's widest (module `.sheet`, `md` and up: `min(100%, 640px)`). */
const SHEET_MAX_W = 640
/** The scroll region's test id: the foot must never be inside it. */
export const PICK_SHEET_BODY_TEST_ID = 'pick-sheet-body'

interface PickSheetProps {
  pick: DSGuidePickRef
  placement: 'roster' | 'section' | 'grid'
  segment: PickSheetSegment
  onSegment: (segment: PickSheetSegment) => void
  onClose: () => void
  /** The row's chips, rendered by the caller (the same element the row shows). */
  chips: React.ReactNode
  /** The lab may have this pick's curves (false once it answered "nothing", or never tested). */
  measurable: boolean
  curves: LabChartPaths | null | undefined
  /** What the panel overlays: null for an undeclared reader, and for the pick they own. */
  declared: DeclaredProduct | null
  yours: boolean
  owners: CardOwnersView | null
  trackOwner: OwnerTrack
}

const PickSheet: React.VFC<PickSheetProps> = ({
  pick,
  placement,
  segment,
  onSegment,
  onClose,
  chips,
  measurable,
  curves,
  declared,
  yours,
  owners,
  trackOwner,
}) => {
  const { context } = pick
  const name = context?.name ?? pick.title
  const lab = useLabCompare()
  const pinned = lab?.pinned.length ?? 0
  const titleId = `guide-sheet-${placement}-${pick.clearBuyProductId}-title`
  const location = placement === 'section' ? 'pick' : placement
  // The Compare chart's frame follows the sheet's own content width — derived, as the drawer
  // does, from the viewport it reads on resize (0 until mounted: compact for one frame).
  const [viewportW, setViewportW] = useState(initialViewportW)
  useEffect(() => {
    const read = (): void => setViewportW(window.innerWidth)
    read()
    window.addEventListener('resize', read)
    return () => window.removeEventListener('resize', read)
  }, [])
  const frame = chartFrameFor(Math.min(viewportW, SHEET_MAX_W) - SHEET_PAD_X)
  const pinnable = Boolean(context && context.lab_tested && !yours)

  const segmentButton = (
    key: PickSheetSegment,
    label: string,
    empty: boolean,
    tip?: string
  ): React.ReactElement => (
    <button
      type="button"
      aria-pressed={segment === key}
      title={empty ? tip : undefined}
      data-segment={key}
      className={clsx(
        styles.segment,
        segment === key && styles.segmentOn,
        empty && styles.segmentEmpty
      )}
      onClick={() => onSegment(key)}
    >
      {label}
    </button>
  )

  let content: React.ReactNode
  if (segment === 'compare') {
    content = (
      <LabCompareBody
        idPrefix={`sheet-${placement}-compare-${pick.clearBuyProductId}`}
        readVerb="Tap"
        frame={frame}
        fullLink={!context}
      />
    )
  } else if (segment === 'owners') {
    content = owners ? (
      <PickOwners
        owners={owners}
        claimedHours={context?.compare.batteryHours ?? null}
        pdpHref={context?.sg_href ?? null}
        nodeId={context?.node_id ?? ''}
        productName={name}
        surface="sheet"
      />
    ) : (
      <p className={styles.empty}>{GUIDE_COPY.actions.ownersEmpty}</p>
    )
  } else {
    let measure: React.ReactNode
    if (curves) {
      measure = (
        <PickLabPanel
          productName={name}
          pdpHref={context?.sg_href ?? null}
          curves={curves}
          declared={declared}
          trackOwner={trackOwner}
          idPrefix={`sheet-${placement}-lab-${pick.clearBuyProductId}`}
          location={location}
        />
      )
    } else {
      // Not measurable (never tested, or the lab answered "nothing") says so; otherwise the
      // answer is still on its way.
      measure = (
        <p className={styles.loading}>
          {curves === undefined && measurable
            ? 'Loading lab curves…'
            : 'No lab measurements to draw.'}
        </p>
      )
    }
    content = (
      <>
        {chips}
        {measure}
      </>
    )
  }

  return (
    <BottomSheet
      labelledBy={titleId}
      onClose={onClose}
      withClose
      contentScroll
      className={styles.sheet}
    >
      <header className={styles.head}>
        <h2 id={titleId} className={styles.title}>
          {name}
        </h2>
        {declared && !yours && <p className={styles.sub}>{GUIDE_COPY.sheet.vs(declared.name)}</p>}
      </header>
      <div className={styles.segments}>
        {segmentButton('measure', GUIDE_COPY.panel.thisPick, !measurable)}
        {segmentButton(
          'owners',
          owners
            ? `${GUIDE_COPY.actions.owners} · ${owners.count}`
            : `${GUIDE_COPY.actions.owners} · ${GUIDE_COPY.actions.ownersNone}`,
          !owners,
          GUIDE_COPY.actions.ownersEmptyTip
        )}
        {/* Outside a lab provider there is no comparison to show: no segment for it. */}
        {lab &&
          segmentButton(
            'compare',
            GUIDE_COPY.sheet.compare(pinned, lab.maxPins),
            pinned === 0,
            GUIDE_COPY.compare.emptyTip
          )}
      </div>
      <div className={styles.body} data-testid={PICK_SHEET_BODY_TEST_ID}>
        {content}
      </div>
      {context && (
        <footer className={styles.foot}>
          {pinnable && (
            <PinToCompare
              nodeId={context.node_id}
              slug={context.full_slug}
              name={context.name}
              pdpHref={context.sg_href}
              clearbuyProductId={context.compare.clearbuyProductId ?? pick.clearBuyProductId}
              requireDeclared={false}
              mode="toggle"
              location={placement}
              variant="tile"
              onBlocked={() => onSegment('compare')}
            />
          )}
          <CompareTile segment={segment} context={context} yours={yours} />
        </footer>
      )}
    </BottomSheet>
  )
}

export default PickSheet
